import { CONSENT_COOKIE_EXPIRY, COOKIE_CONFIG } from '@/config/cookie.config';
import { getCookie, setCookie } from '@/lib/cookies';
import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * @typedef {Object} CookieConsent
 * @property {boolean} analytics - Analytics cookies consent
 * @property {boolean} marketing - Marketing cookies consent
 * @property {boolean} necessary - Necessary cookies consent (always true)
 */

/**
 * @typedef {Object} CookieConsentContextValue
 * @property {CookieConsent|null} consent - Current consent preferences
 * @property {boolean} showBanner - Whether to show the consent banner
 * @property {function(CookieConsent): void} updateConsent - Update consent preferences
 * @property {function(): void} acceptAll - Accept all cookies
 * @property {function(): void} rejectAll - Reject all optional cookies
 * @property {function(): void} resetConsent - Reset consent preferences
 */

const CookieConsentContext = createContext(
  /** @type {CookieConsentContextValue|undefined} */ (undefined)
);

/**
 * Cookie Consent Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(
    /** @type {CookieConsent|null} */ (null)
  );
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check for existing consent
    const savedConsent = getCookie(COOKIE_CONFIG.CONSENT);

    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
      } catch (error) {
        console.error('Error parsing consent cookie:', error);
        setShowBanner(true);
      }
    } else {
      // No consent found, show banner
      setShowBanner(true);
    }
  }, []);

  /**
   * Update consent preferences
   * @param {CookieConsent} newConsent - New consent preferences
   */
  const updateConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      necessary: true, // Always true
      timestamp: new Date().toISOString(),
    };

    // Save to cookie
    setCookie(COOKIE_CONFIG.CONSENT, JSON.stringify(consentData), {
      maxAge: CONSENT_COOKIE_EXPIRY * 24 * 60 * 60,
    });

    setConsent(consentData);
    setShowBanner(false);
  };

  /**
   * Accept all cookies
   */
  const acceptAll = () => {
    updateConsent({
      analytics: true,
      marketing: true,
      necessary: true,
    });
  };

  /**
   * Reject all optional cookies
   */
  const rejectAll = () => {
    updateConsent({
      analytics: false,
      marketing: false,
      necessary: true,
    });
  };

  /**
   * Reset consent preferences (for testing or settings)
   */
  const resetConsent = () => {
    setCookie(COOKIE_CONFIG.CONSENT, '', { maxAge: -1 });
    setConsent(null);
    setShowBanner(true);
  };

  const value = {
    consent,
    showBanner,
    updateConsent,
    acceptAll,
    rejectAll,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

/**
 * Hook to use cookie consent context
 * @returns {CookieConsentContextValue} Cookie consent context value
 * @throws {Error} If used outside of CookieConsentProvider
 */
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);

  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider'
    );
  }

  return context;
};
